import {
  Avatar,
  Box,
  Collapse,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Stack,
  Tooltip,
  theme,
  useColorMode,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";

import { useContext, useEffect, useState } from "react";
import { fetchController } from "../../utils/FetchController/fetchController";
import { AiOutlineAim } from "react-icons/ai";
import { FaRobot } from "react-icons/fa";
import { LiaMoneyBillWaveAltSolid, LiaToolsSolid } from "react-icons/lia";
import {
  MdDataUsage,
  MdFormatListNumbered,
  MdLiveHelp,
  MdLock,
  MdOutlineDarkMode,
  MdOutlineLogout,
  MdOutlineWebhook,
  MdSupportAgent,
} from "react-icons/md";
import { SunIcon } from "@chakra-ui/icons";
import { GrPhone, GrClose } from "react-icons/gr";
import CallDropDownModal from "./CallDropDownModal";
import { useLocation, useNavigate } from "react-router-dom";

import { IoKeyOutline, IoLibraryOutline, IoSyncSharp } from "react-icons/io5";
import {
  liveAgents,
  navItemsData_1,
  navItemsData_5,
} from "../../utils/Data/data";
import AuthContext from "../Auth/AuthContext";
import InstructionModal from "../Modals/InstructionModal";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import NavItem from "./NavItem";
import { NAVBAR_ITEMS } from "./contants";
import { LuNetwork, LuSquareFunction } from "react-icons/lu";
import { FaRegCircleUser } from "react-icons/fa6";
import { TbApi } from "react-icons/tb";
import { GrSystem } from "react-icons/gr";
import { HiOutlineUserGroup } from "react-icons/hi";
import { RiDiscordLine } from "react-icons/ri";
import { BiSupport } from "react-icons/bi";
import { FiChevronDown, FiChevronUp, FiExternalLink } from "react-icons/fi";
import SidebarWallet from "./SidebarWallet";
import FeatureUpgradeModal from "../shared/FeatureUpgradeModal";

export function SidebarUserDetails(props) {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const collapsed = Boolean(props?.collapsed);

  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen: dialerOpen, onToggle: onToggleDialer, onClose: onCloseDialer } = useDisclosure();
  // Controlled-menu disclosure so navigation icons in the bottom strip
  // can close the popup while inline toggles (dialer, theme) keep it open.
  const { isOpen: menuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure();

  const { state } = useContext(SettingContext);
  const hasHumanAgents = state?.credits?.human_agents > 0;
  const isByokEntitled = state?.credits?.byok > 0;

  // Submenu open/close for the Logs + Resources clusters in the popup.
  const [logsOpen, setLogsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);

  // Feature-gated modal state. `isOnAgency` reads the agency context
  // lower in the function — set below after the destructure.
  const [gatingFor, setGatingFor] = useState(null);

  // BYOK "lit" indicator on the BYOK Credentials menu row: only true
  // when the user has explicitly enabled BYOK via the "Use my keys"
  // checkbox on the BYOK settings page — that toggle is persisted as
  // `status: true` on the user_provider_key record (see YourKeys.js's
  // setUseKeys binding). Skip the API call when the plan doesn't
  // include BYOK.
  const [byokActive, setByokActive] = useState(false);
  useEffect(() => {
    if (!isByokEntitled) {
      setByokActive(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchController(
          "/api/v1/user_provider_key/list?page=1&size=50",
          "GET"
        );
        const items = res?.data?.items || [];
        const enabled = items.some((it) => it?.status === true);
        if (!cancelled) setByokActive(enabled);
      } catch {
        if (!cancelled) setByokActive(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isByokEntitled]);

  const logoutUser = () => {
    logout();
    localStorage.removeItem("announced");
  };

  const agency = useContext(AgencyContext);

  const { buttonColorScheme, myDetails } = agency;

  // On a white-label agency we don't know which plans the agency
  // offers, so the FeatureUpgradeModal omits the plan-badge row and
  // shows a generic upgrade nudge instead.
  const isOnAgency = Boolean(myDetails?.agency_id);
  const handleGatedClick = (item) => {
    const feature = item?.requires_feature;
    const entitled = !feature || state?.credits?.[feature];
    if (entitled) {
      handleMenuClick(item.path);
      return;
    }
    setGatingFor(item);
    onMenuClose();
  };

  const isDocsAndDiscordVisible = agency.loading
    ? false
    : myDetails?.agency_id
    ? myDetails?.branding?.socials?.discord ||
      myDetails?.branding?.socials?.docs
      ? true
      : false
    : true;

  const renderUserName = () => {
    const { navSize, userName, user } = props;

    if (navSize !== "small") {
      if (userName) {
        return userName;
      }
      if (user?.email) {
        return user.email;
      }
      return "Your Account";
    } else {
      return "";
    }
  };
  const handleMenuClick = (path) => {
    navigate(path);
  };

  // Account = identity + money. Reads as "things about me".
  const accountItems = [
    { label: "Profile", icon: FaRegCircleUser, path: "/settings/profile" },
    { label: "Teams", icon: HiOutlineUserGroup, path: "/settings/teams" },
    {
      label: agency?.myDetails?.agency_id ? "Billing" : "Billing & Wallet",
      icon: LiaMoneyBillWaveAltSolid,
      path: "/settings/billing",
    },
    { label: "Usage", icon: MdDataUsage, path: "/settings/usage" },
  ];

  // Developer = keys + outbound integration plumbing.
  // "Personal API Tokens" = tokens you use to call Insighto's API.
  // "BYOK Credentials"    = your OpenAI/Azure/Deepgram keys that
  //                         Insighto uses on your behalf.
  const developerItems = [
    {
      label: "Personal API Tokens",
      icon: TbApi,
      path: "/settings/api-keys",
    },
    {
      label: "BYOK Credentials",
      icon: IoKeyOutline,
      path: "/settings/your-keys",
      indicator: byokActive,
      requires_feature: "byok",
      upgrade_description:
        "Use your own OpenAI / Azure / Deepgram credentials so Insighto calls those providers on your behalf. Avoid rate limits and pick the exact models you want.",
      upgrade_plans: ["Ultra", "Agency Pro"],
    },
    { label: "Webhooks", icon: MdOutlineWebhook, path: "/settings/webhooks" },
  ];

  // Operations always renders; feature gating shows a lock + upgrade
  // modal when the user lacks the corresponding credit. (No "active"
  // indicator here — entitlement and active-use are the same signal
  // for Human Agents, so the dot would be redundant noise.)
  const operationsItems = [
    {
      label: "Manage Human Agents",
      icon: MdSupportAgent,
      path: "/settings/agents",
      requires_feature: "human_agents",
      upgrade_description:
        "Hand off conversations from your AI assistant to a live human teammate. Includes the live-agent inbox, conversation routing, and presence indicators.",
      upgrade_plans: ["Ultra", "Agency Pro"],
    },
  ];

  const logsItems = [
    { label: "System Logs", icon: GrSystem, path: "/logs/system_log" },
    {
      label: "Tool Function Calls",
      icon: LuSquareFunction,
      path: "/logs/tool_function_invoke_log",
    },
    {
      label: "CRM Sync",
      icon: IoSyncSharp,
      path: "/logs/contact_sync_log",
    },
  ];

  const onClickDiscord = () => {
    const link =
      agency?.myDetails?.branding?.socials?.discord ||
      "https://discord.gg/3baqJDqGYQ";
    window.open(link, "_blank");
  };
  const onClickFreshDesk = () => {
    const link =
      agency?.myDetails?.branding?.socials?.docs || "https://docs.insighto.ai/";
    window.open(link, "_blank");
  };
  const onClickAffiliate = () => {
    window.open("https://affiliates.insighto.ai/program", "_blank");
  };

  const menuItems = [
    {
      label: "Discord",
      value: onClickDiscord,
      icon: RiDiscordLine,
      isVisible: agency?.myDetails?.branding?.socials?.discord || true,
    },
    {
      label: "Docs",
      value: onClickFreshDesk,
      icon: BiSupport,
      isVisible: agency?.myDetails?.agency_id ? false : true,
    },
    {
      label: "Affiliate ",
      value: onClickAffiliate,
      icon: FiExternalLink,
      isVisible: agency?.myDetails?.agency_id ? false : true,
    },
  ];
  const filterMenuItem = menuItems.filter((item) => item.isVisible);
  const isActive = (path) =>
    window.location.pathname.includes(path) ? true : false;
  const textColor = agency?.textColor;
  const iconColor = useColorModeValue(
    textColor || theme.colors.gray[500],
    "white"
  );
const isSalesBtnisvisible = agency.loading
  ? false
  : myDetails?.agency_id
  ? myDetails?.wallet === true
  : true;

  // Render a single menu row, with optional feature-gate, active
  // green dot, and locked styling.
  const renderRow = (item) => {
    const feature = item?.requires_feature;
    const locked = feature && !state?.credits?.[feature];
    return (
      <MenuItem
        key={item.label}
        icon={<Icon as={item.icon} />}
        onClick={() => handleGatedClick(item)}
        borderRight={isActive(item.path) && `3px solid ${iconColor}`}
        opacity={locked ? 0.65 : 1}
        color={
          colorMode === "light"
            ? isActive(item.path)
              ? textColor
              : "black"
            : isActive(item.path)
            ? "white"
            : "gray.300"
        }
      >
        <Flex align="center" justify="space-between" w="100%" gap={2}>
          <Box as="span">{item.label}</Box>
          <HStack spacing={1}>
            {item.indicator && !locked && (
              <Tooltip label="Active">
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="green.400"
                  boxShadow="0 0 4px rgba(72, 187, 120, 0.7)"
                />
              </Tooltip>
            )}
            {locked && (
              <Box color="gray.400" display="flex" alignItems="center">
                <MdLock size={12} />
              </Box>
            )}
          </HStack>
        </Flex>
      </MenuItem>
    );
  };

  return (
    <Grid w={"100%"} gap={2} p={2}>
      {isSalesBtnisvisible && !collapsed && (
        <SidebarWallet />
      )}

      {dialerOpen && (
        <Box
          position="fixed"
          // bottom="70px"
          top="80px"
          left="500px"
          zIndex={200}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          p={2}
          bg={colorMode === "dark" ? "gray.800" : "white"}
          width="350px"
          onClick={(e) => e.stopPropagation()} 
        >
          <CallDropDownModal />
        </Box>
      )}

      <Stack
        direction="row"
        spacing={1}
        width={"100%"}
        align="center"
        justify={collapsed ? "center" : "flex-start"}
      >
        {/* ─── Quick action icons on the LEFT (hidden when collapsed) ─── */}
        {!collapsed && (
          <>
            <Tooltip label={dialerOpen ? "Close dialer" : "Dialer"}>
              <IconButton
                aria-label="Dialer"
                icon={dialerOpen ? <GrClose /> : <GrPhone />}
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDialer();
                }}
              />
            </Tooltip>
            <Tooltip
              label={colorMode === "light" ? "Dark mode" : "Light mode"}
            >
              <IconButton
                aria-label="Toggle theme"
                icon={
                  colorMode === "light" ? (
                    <MdOutlineDarkMode />
                  ) : (
                    <SunIcon />
                  )
                }
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleColorMode();
                }}
              />
            </Tooltip>
            <Tooltip label="Support">
              <IconButton
                aria-label="Support"
                icon={<MdLiveHelp />}
                size="sm"
                variant="ghost"
                onClick={() => {
                  window.location.href = `mailto:${
                    agency?.myDetails?.branding?.agency_mail ||
                    "support@insighto.ai"
                  }`;
                }}
              />
            </Tooltip>

            <Box flex={1} />
          </>
        )}

        {/* ─── Avatar trigger on the RIGHT ─── */}
        <Menu
          placement="right-end"
          closeOnBlur={!dialerOpen}
          isOpen={menuOpen}
          onOpen={onMenuOpen}
          onClose={onMenuClose}
        >
          <Tooltip label={renderUserName() || "Account"} placement="top">
            <MenuButton
              as={Box}
              cursor="pointer"
              borderRadius="full"
              _hover={{ opacity: 0.85 }}
            >
              <Avatar
                size="sm"
                name={
                  props.user?.first_name
                    ? `${props.user.first_name} ${props.user.last_name || ""}`.trim()
                    : props.user?.email
                }
                bg={
                  buttonColorScheme ? `${buttonColorScheme}.500` : "blue.500"
                }
                color="white"
              />
            </MenuButton>
          </Tooltip>
          <MenuList maxH="98vh" overflow="hidden" p={0}>
            <Box
              maxH="98vh"
              overflowY="auto"
              className="sidebar-menu-scroll"
            >
              {/* ─── Account ─── */}
              <MenuGroup title="Account">
                {accountItems.map(renderRow)}
              </MenuGroup>

              <MenuDivider />

              {/* ─── Developer ─── */}
              <MenuGroup title="Developer">
                {developerItems.map(renderRow)}
              </MenuGroup>

              <MenuDivider />

              {/* ─── Operations ─── */}
              <MenuGroup title="Operations">
                {operationsItems.map(renderRow)}
              </MenuGroup>

              <MenuDivider />

              {/* ─── Logs (collapsible submenu) ─── */}
              <MenuItem
                icon={<Icon as={GrSystem} />}
                closeOnSelect={false}
                onClick={(e) => {
                  e.stopPropagation();
                  setLogsOpen((v) => !v);
                }}
              >
                <Flex align="center" justify="space-between" w="100%">
                  <Box as="span">Logs</Box>
                  <Box display="flex" alignItems="center" color="gray.400">
                    {logsOpen ? <FiChevronUp /> : <FiChevronDown />}
                  </Box>
                </Flex>
              </MenuItem>
              <Collapse in={logsOpen} animateOpacity>
                <Box pl={2}>
                  {logsItems.map((item) => (
                    <MenuItem
                      key={item.label}
                      icon={<Icon as={item.icon} />}
                      onClick={() => handleMenuClick(item.path)}
                      pl={6}
                      fontSize="sm"
                      borderRight={
                        isActive(item.path) && `3px solid ${iconColor}`
                      }
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Box>
              </Collapse>

              {/* ─── Resources (single-row icon strip, conditional) ─── */}
              {isDocsAndDiscordVisible && filterMenuItem.length > 0 && (
                <>
                  <MenuItem
                    icon={<Icon as={BiSupport} />}
                    closeOnSelect={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      setResourcesOpen((v) => !v);
                    }}
                  >
                    <Flex align="center" justify="space-between" w="100%">
                      <Box as="span">Resources</Box>
                      <Box display="flex" alignItems="center" color="gray.400">
                        {resourcesOpen ? <FiChevronUp /> : <FiChevronDown />}
                      </Box>
                    </Flex>
                  </MenuItem>
                  <Collapse in={resourcesOpen} animateOpacity>
                    <HStack pl={10} pr={3} py={2} spacing={1}>
                      {filterMenuItem.map((item) => (
                        <Tooltip key={item.label} label={item.label}>
                          <IconButton
                            aria-label={item.label}
                            icon={<Icon as={item.icon} />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              item.value();
                              onMenuClose();
                            }}
                          />
                        </Tooltip>
                      ))}
                    </HStack>
                  </Collapse>
                </>
              )}

              <MenuDivider />
              <MenuItem
                icon={<MdOutlineLogout />}
                onClick={logoutUser}
                color="red.500"
              >
                Logout
              </MenuItem>
            </Box>
          </MenuList>
        </Menu>
      </Stack>

      {/* Upgrade nudge modal for gated popup rows. On a white-label
          agency we hide the plan-name badges since we can't enumerate
          the agency's own plan lineup; the modal shows just the
          description + a generic "View plans" CTA. */}
      {gatingFor && (
        <FeatureUpgradeModal
          isOpen={Boolean(gatingFor)}
          onClose={() => setGatingFor(null)}
          title={gatingFor.label}
          description={gatingFor.upgrade_description}
          availablePlans={isOnAgency ? [] : gatingFor.upgrade_plans || []}
        />
      )}
    </Grid>
  );
}

export default function SideBar() {
  const [navSize] = useState("large");
  const navigate = useNavigate();

  const { authUser, logout } = useContext(AuthContext);

  const { isOpen, onClose } = useDisclosure();
  const handleItemClick = (path) => {
    navigate(path);
  };
  const onClickLogout = () => {
    logout();
  };

  const userName =
    authUser?.first_name?.length > 15
      ? `${authUser?.first_name?.slice(0, 10)}...`
      : authUser?.first_name;

  function AIAgentsTab() {
    const location = useLocation();
    return (
      <Box borderRadius={"md"}>
        <Box onClick={() => handleItemClick("/ai-agents")}>
          <NavItem
            toolTip={
              "AI Agent helps you add your dataset, train your assistant, configure intents & launch widgets with ease."
            }
            navSize={navSize}
            icon={FaRobot}
            active={location.pathname.startsWith("/ai-agents")}
            title={"AI Agents"}
          />
        </Box>
        {NAVBAR_ITEMS.some((path) => location.pathname.startsWith(path)) ? (
          <ul
            style={{
              listStyle: "none",
              paddingLeft: 10,
            }}
          >
            <li onClick={() => handleItemClick("/conversation-flow")}>
              <NavItem
                size="small"
                toolTip={"Configure conversation flow"}
                navSize={navSize}
                icon={LuNetwork}
                active={location.pathname.startsWith("/conversation-flow")}
                title={"Conversation Flow"}
              />
            </li>
            <li onClick={() => handleItemClick("/forms")}>
              <NavItem
                size="small"
                toolTip={"Configure the forms"}
                navSize={navSize}
                icon={MdFormatListNumbered}
                active={location.pathname.startsWith("/forms")}
                title={"Forms"}
              />
            </li>
            <li onClick={() => handleItemClick("/intents")}>
              <NavItem
                size="small"
                toolTip={"Configure your intents here"}
                navSize={navSize}
                icon={AiOutlineAim}
                active={location.pathname.startsWith("/intents")}
                title={"Intents"}
              />
            </li>
            <li onClick={() => handleItemClick("/tools")}>
              <NavItem
                size="small"
                toolTip={"Configure different tools"}
                navSize={navSize}
                icon={LiaToolsSolid}
                active={location.pathname.startsWith("/tools")}
                title={"Tools"}
              />
            </li>
            <li onClick={() => handleItemClick("/library/prompts")}>
              <NavItem
                size="small"
                toolTip={"Configure libraries"}
                navSize={navSize}
                icon={IoLibraryOutline}
                active={location.pathname.startsWith("/library")}
                title={"Library"}
              />
            </li>
            <li onClick={() => handleItemClick("/voices")}>
              <NavItem
                size="small"
                toolTip={"Configure Voices"}
                navSize={navSize}
                icon={LiaToolsSolid}
                active={location.pathname.startsWith("/voices")}
                title={"Voices"}
              />
            </li>
          </ul>
        ) : null}
      </Box>
    );
  }

  const agency = useContext(AgencyContext);
  const { myDetails } = agency;
  const agencyLogoUrl = myDetails ? myDetails?.branding?.logo_url : null;
  const logo = !agency.loading ? (
    myDetails?.agency_id ? (
      agencyLogoUrl ? (
        <Image
          width="100px"
          src={
            agencyLogoUrl.includes(window.origin)
              ? agencyLogoUrl
              : `${window.location.origin}${agencyLogoUrl}`
          }
          alt={`${myDetails?.branding?.agency_name}-logo`}
        />
      ) : null
    ) : (
      <Image width="150px" src="/logo/indightoai.png" alt="Insighto.ai" />
    )
  ) : null;
  const userRoles = authUser?.roles || [];
  const isAgent = userRoles.includes("agent") && userRoles?.length === 1;
  return (
    <div className="sidebar">
      <Flex
        h={"100svh"}
        boxShadow="md"
        borderRadius={navSize === "small" ? "8px" : "8px"}
        w={navSize === "small" ? "75px" : "240px"}
        flexDir="column"
        overflowY={"auto"}
        justifyContent="space-between"
      >
        <Grid>
          <Flex p={2} justifyContent={"center"} alignItems="center" gap={3}>
            <div
              style={{
                display: navSize === "small" ? "none" : "block",
                color: "#3182ce",
              }}
            >
              {logo}
            </div>
          </Flex>
          <div>
            {!isAgent ? (
              <>
                {navItemsData_1.map((item, index) => (
                  <div key={index} onClick={() => handleItemClick(item.path)}>
                    <NavItem
                      toolTip={item.toolTip}
                      navSize={navSize}
                      icon={item.icon}
                      active={item.path === window.location.pathname}
                      title={item.title}
                      description={item.description}
                    />
                  </div>
                ))}
                <hr />
                <AIAgentsTab />
                <hr />
                {navItemsData_5
                  .filter((item) =>
                    agency?.myDetails?.agency_id
                      ? item.title !== "Agency" && item.title !== "Campaigns"
                      : true
                  )
                  .map((item, index) => (
                    <div key={index} onClick={() => handleItemClick(item.path)}>
                      <NavItem
                        toolTip={item.toolTip}
                        navSize={navSize}
                        icon={item.icon}
                        active={window.location.pathname.includes(item.path)}
                        title={item.title}
                        description={item.description}
                      />
                    </div>
                  ))}
              </>
            ) : null}
            <hr />
            {liveAgents.map((item, index) => (
              <div key={index} onClick={() => handleItemClick(item.path)}>
                <NavItem
                  toolTip={item.toolTip}
                  navSize={navSize}
                  icon={item.icon}
                  active={window.location.pathname.includes(item.path)}
                  title={item.title}
                  description={item.description}
                />
              </div>
            ))}
            <hr />
          </div>
        </Grid>
        {!isAgent ? (
          <>
            <InstructionModal onClose={onClose} isOpen={isOpen} />
            <SidebarUserDetails
              navSize={navSize}
              user={authUser}
              onClickLogout={onClickLogout}
              userName={userName}
            />
          </>
        ) : null}
      </Flex>
    </div>
  );
}
